import EmptyState from '../../../../components/ui/EmptyState';
import type { PrescriptionPrintTemplateProps } from '../types';
import DelegationLine from '../../components/DelegationLine';
import { delegatedSignatureLine, effectiveSignature } from '../../hooks/delegatedSignature';

export function BasePrescriptionTemplate({
  consultation,
  doctor,
  patient,
  prescriptions,
  clinicName,
  clinicAddress,
  clinicLogo,
  letterheadUrl,
  includeLetterhead = true,
  formatDate,
  calculateAge,
}: PrescriptionPrintTemplateProps) {
  const age = patient?.birth_date ? calculateAge(patient.birth_date) : null;
  const showLetterhead = !!letterheadUrl && includeLetterhead;

  return (
    <>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; color: #111; background: white; }
        .page { width: 100%; max-width: 148mm; margin: 0 auto; padding: 14mm 16mm; min-height: 210mm; position: relative; }
        .letterhead-bg { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: -1; object-fit: contain; pointer-events: none; opacity: 0.95; }
        .page-content { position: relative; z-index: 1; }
        .header { border-bottom: 3px double #111; padding-bottom: 10px; margin-bottom: 14px; }
        .doctor-name { font-size: 16pt; font-weight: bold; }
        .doctor-sub { font-size: 10pt; color: #333; line-height: 1.6; }
        .clinic-sub { font-size: 9pt; color: #555; margin-top: 4px; }
        .meta { display: flex; justify-content: space-between; margin-bottom: 14px; font-size: 10pt; }
        .patient-info { border-bottom: 1px solid #aaa; padding-bottom: 8px; margin-bottom: 16px; }
        .patient-label { font-size: 9pt; color: #555; }
        .patient-name { font-size: 12pt; font-weight: bold; }
        .rx-symbol { font-size: 28pt; font-weight: bold; color: #1d4ed8; margin-bottom: 16px; line-height: 1; }
        .rx-item { margin-bottom: 18px; page-break-inside: avoid; }
        .rx-num { color: #1d4ed8; font-weight: bold; font-size: 12pt; margin-right: 4px; }
        .rx-name { font-size: 13pt; font-weight: bold; }
        .rx-dose { font-size: 12pt; color: #333; margin-left: 2px; }
        .rx-sig { font-size: 10pt; color: #444; margin-left: 20px; margin-top: 3px; font-style: italic; }
        .rx-instructions { font-size: 10pt; color: #555; margin-left: 20px; margin-top: 2px; }
        .no-rx { text-align: center; color: #888; padding: 30px; font-style: italic; }
        .divider { border-top: 1px dashed #aaa; margin: 16px 0; }
        .indications { font-size: 10pt; line-height: 1.7; white-space: pre-wrap; }
        .followup { font-size: 10pt; margin-top: 8px; }
        .footer { margin-top: 40px; }
        .signature-line { border-top: 1px solid #111; width: 180px; margin-bottom: 4px; }
        .signature-name { font-size: 11pt; font-weight: bold; }
        .signature-sub { font-size: 9pt; color: #555; }
        @media print {
          @page { margin: 10mm 12mm; size: A5; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          img { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print" style={{ background: '#f0f0f0', padding: '10px 20px', display: 'flex', gap: 12, alignItems: 'center' }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
          Imprimir / Guardar PDF
        </button>
        <button onClick={() => window.close()} style={{ padding: '6px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          Cerrar
        </button>
      </div>

      {showLetterhead && (
        <img
          className="letterhead-bg no-print"
          src={letterheadUrl}
          alt=""
          aria-hidden="true"
        />
      )}

      <div className="page">
        <div className="header">
          {clinicLogo && (
            <div style={{ marginBottom: 8 }}>
              <img
                src={clinicLogo}
                alt="Logo de la clínica"
                style={{ maxWidth: '120px', height: 'auto', printColorAdjust: 'exact' }}
              />
            </div>
          )}
          <div className="doctor-name">Dr. {doctor?.first_name} {doctor?.last_name}</div>
          <div className="doctor-sub">
            {doctor?.specialty && <span>{doctor.specialty.name}</span>}
            {doctor?.license_number && <span> &nbsp;|&nbsp; Exequátur: {doctor.license_number}</span>}
            {doctor?.phone && <span> &nbsp;|&nbsp; Tel: {doctor.phone}</span>}
          </div>
          {clinicAddress && <div className="clinic-sub">{clinicName} &nbsp;·&nbsp; {clinicAddress}</div>}
          {!clinicAddress && clinicName !== 'eCliniq' && <div className="clinic-sub">{clinicName}</div>}
        </div>

        <div className="meta">
          <span>{clinicAddress || 'Santo Domingo'}, {formatDate(consultation.consultation_date)}</span>
        </div>

        <div className="patient-info">
          <div className="patient-label">Paciente</div>
          <div className="patient-name">{patient?.first_name} {patient?.last_name}</div>
          <div style={{ fontSize: '10pt', color: '#444', marginTop: 2 }}>
            {patient?.identification && <span>Cédula: {patient.identification}</span>}
            {age !== null && <span style={{ marginLeft: 12 }}>Edad: {age} años</span>}
          </div>
        </div>

        <div className="rx-symbol">℞</div>

        {prescriptions.length === 0 ? (
          <EmptyState icon="fa-file-lines" title="No hay medicamentos prescritos en esta consulta" className="py-4" />
        ) : (
          prescriptions.map((prescription, index) => (
            <div className="rx-item" key={prescription.id}>
              <div>
                <span className="rx-num">{index + 1}.</span>
                <span className="rx-name">{prescription.medication_name}</span>
                {prescription.dosage && <span className="rx-dose">{prescription.dosage}</span>}
              </div>
              <div className="rx-sig">
                Sig: {prescription.frequency}
                {prescription.duration && ` — ${prescription.duration}`}
              </div>
              {prescription.instructions && (
                <div className="rx-instructions">{prescription.instructions}</div>
              )}
              <DelegationLine text={delegatedSignatureLine(effectiveSignature(prescription, consultation), doctor)} />
            </div>
          ))
        )}

        {(consultation.general_recommendations || consultation.follow_up_date) && <div className="divider" />}

        {consultation.general_recommendations && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '9pt', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, color: '#555', marginBottom: 4 }}>
              Indicaciones
            </div>
            <div className="indications">{consultation.general_recommendations}</div>
          </div>
        )}

        {consultation.follow_up_date && (
          <div className="followup">
            <strong>Próxima cita:</strong> {formatDate(consultation.follow_up_date)}
          </div>
        )}

        <div className="footer">
          <div className="signature-line"></div>
          <div className="signature-name">Dr. {doctor?.first_name} {doctor?.last_name}</div>
          {doctor?.specialty && <div className="signature-sub">{doctor.specialty.name}</div>}
          {doctor?.license_number && (
            <div className="signature-sub">Exequátur: {doctor.license_number}</div>
          )}
        </div>
      </div>
    </>
  );
}
